import { cn } from '~/lib/cn';

interface ProgressCircleProps {
  /** The percent to display. */
  percent: number;

  /** The size of the circle in pixels. */
  size?: number;

  /** The width of the stroke in pixels. */
  strokeWidth?: number;

  /** The class name for the background circle. */
  backgroundCircleClassName?: string;

  /** The class name for the percent circle. */
  percentCircleClassName?: string;
}

/**
 * Component that displays a progress percent in a circle. The top of the circle
 * is 0% and the percent increases clockwise.
 *
 * @param percent - The percent to display.
 * @param size - The size of the circle in pixels.
 * @param strokeWidth - The width of the stroke in pixels.
 * @param backgroundCircleClassName - The class name for the background circle.
 * @param percentCircleClassName - The class name for the percent circle.
 *
 * @returns The ProbressCircle component.
 */
function ProgressCircle({
  percent,
  size = 18,
  strokeWidth = 4,
  backgroundCircleClassName,
  percentCircleClassName,
}: ProgressCircleProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);
  const center = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* The background circle that the percent circle is drawn on top of. */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        strokeWidth={strokeWidth}
        fill="transparent"
        // NOTE: Because of the way we use Tailwing V3 and oklch, there are custom
        //       stroke colours in tailwind.config.js (see the "extend" section).
        className={cn('stroke-emphasis-strong', backgroundCircleClassName)}
      />

      {/* The circle that represents the Percent value. */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${center} ${center})`}
        // NOTE: Because of the way we use Tailwing V3 and oklch, there are custom
        //       stroke colours in tailwind.config.js (see the "extend" section).
        className={cn('stroke-default', percentCircleClassName)}
      />
    </svg>
  );
}

ProgressCircle.displayName = 'ProgressCircle';

export { ProgressCircle };
